/**
 * posNetAmount.js
 *
 * POS hesabına yapılan tahsilatlarda "aktarılan tutar" (net_amount) zorunlu
 * hale gelir; kasa/banka/türsüz hesaplarda opsiyonel kalır (boş -> backend
 * ödenen tutara eşitler). Bu saf yardımcı, üç giriş noktasında (CollectModal
 * — taksit tahsilatı, NoteCollectModal — senet tahsilatı, CollectionForm —
 * admin_add_student_payment) AYNI kuralı taşımak için var; mantık tek yerde
 * durur, her form kendi state şekline (ham useState string / react-hook-form)
 * göre bu fonksiyonları çağırır.
 *
 * Backend sözleşmesi (routes/payments.py, services/payment_net.py):
 *   - account_type alanı yoksa hesap "bank" sayılır (eski kayıt).
 *   - POS hesabına tahsilatta net_amount: zorunlu, > 0, <= ödenen tutar (+0.005 tolerans).
 *   - Kasa/banka/türsüz hesapta net_amount: opsiyonel; boşsa backend null alır
 *     ve okuma tarafı ödenen tutara ($ifNull) düşer.
 */

/** account_type alanından POS olup olmadığını döner. Alan yoksa/boşsa "bank" sayılır. */
export function isPosAccountType(accountType) {
  return (accountType || 'bank') === 'pos';
}

/**
 * `bank_id` ile seçilen hesabı `banks` listesinden bulup POS olup olmadığını
 * döner. Hesap bulunamazsa (henüz seçilmediyse) `false` — zorunluluk yalnızca
 * gerçekten POS seçiliyken devreye girer.
 */
export function isSelectedBankPos(banks, bankId) {
  if (!bankId) return false;
  const bank = (banks || []).find((b) => b.bank_id === bankId);
  if (!bank) return false;
  return isPosAccountType(bank.account_type);
}

/**
 * net_amount (aktarılan tutar) doğrulaması.
 *
 * @param {object} args
 * @param {number} args.amount - ödenen (brüt) tutar.
 * @param {string|number} args.netAmountRaw - ham input değeri ('' boş demektir).
 * @param {boolean} args.isPos - hedef hesap POS mu.
 * @returns {'required_pos'|'zero'|'invalid'|'exceeds'|null} hata anahtarı, geçerliyse null.
 *
 * Not: net_amount açıkça gönderildiğinde (boş değil) 0 veya negatif değer
 * hesap türünden bağımsız olarak 'zero' döner — backend kuralıyla birebir.
 * Boş alan yalnızca POS'ta zorunludur (isPos=true -> 'required_pos'); kasa/
 * banka'da boş geçerli kalır (backend ödenen tutara eşitler).
 */
export function validateNetAmount({ amount, netAmountRaw, isPos }) {
  const empty = netAmountRaw === '' || netAmountRaw === undefined || netAmountRaw === null;
  if (empty) {
    return isPos ? 'required_pos' : null;
  }
  const num = Number(netAmountRaw);
  if (Number.isNaN(num)) return 'invalid';
  // Backend (services/payment_net.py) açıkça gönderilen net_amount <= 0
  // değerini hesap türünden bağımsız olarak reddeder — web kuralı bunu
  // birebir yansıtır (yalnızca POS'ta değil, kasa/banka'da da).
  if (num <= 0) return 'zero';
  if (num > Number(amount) + 0.005) return 'exceeds';
  return null;
}

/** Hata anahtarını i18n key'ine çevirir (`t()` ile birlikte kullanılır). */
export function netAmountErrorI18nKey(errorKey) {
  switch (errorKey) {
    case 'required_pos':
      return 'accounting.collect_form.errors.net_settled_amount_required_pos';
    case 'zero':
      return 'accounting.collect_form.errors.net_settled_amount_zero';
    case 'exceeds':
      return 'accounting.collect_form.errors.net_settled_amount_exceeds_amount';
    case 'invalid':
    default:
      return 'accounting.collect_form.errors.net_settled_amount_invalid';
  }
}

/**
 * Komisyon = ödenen − aktarılan. `netAmountRaw` boş/NaN ise `null` döner
 * (canlı satır o durumda gösterilmez).
 */
export function computeCommission(amount, netAmountRaw) {
  if (netAmountRaw === '' || netAmountRaw === undefined || netAmountRaw === null) return null;
  const num = Number(netAmountRaw);
  if (Number.isNaN(num)) return null;
  return Math.round((Number(amount) - num) * 100) / 100;
}
